import { getPublicRuntimeConfig } from "@/lib/config/public-runtime-config";

type Json = Record<string, unknown> | unknown[] | null;

export class HttpError extends Error {
  status: number;
  body?: Json;

  constructor(message: string, status: number, body?: Json) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.body = body;
  }
}

const getApiBaseUrl = (() => {
  let cached: string | null = null;
  return () => {
    if (cached) {
      return cached;
    }

    cached = getPublicRuntimeConfig().apiUrl;
    return cached;
  };
})();

const normalizeJson = (value: unknown): Json => {
  if (value == null) return null;
  if (Array.isArray(value)) return value as unknown[];
  if (typeof value === "object") return value as Record<string, unknown>;
  return null;
};

async function request<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });

  const contentType = res.headers.get("Content-Type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson
    ? normalizeJson((await res.json().catch(() => null)) as unknown)
    : null;

  if (!res.ok) {
    throw new HttpError(res.statusText || "HTTP Error", res.status, data);
  }

  return data as T;
}

export { request };

export const http = {
  get: <T = unknown>(path: string, init: RequestInit = {}) =>
    request<T>(path, { ...init, method: "GET" }),
  post: <T = unknown>(path: string, body?: unknown, init: RequestInit = {}) =>
    request<T>(path, {
      ...init,
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T = unknown>(path: string, body?: unknown, init: RequestInit = {}) =>
    request<T>(path, {
      ...init,
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  del: <T = unknown>(path: string, init: RequestInit = {}) =>
    request<T>(path, { ...init, method: "DELETE" }),
};
